import React from 'react';
import DataTable from './DataTable';

export function AuditLogsManagementTable({
  logs = [],
  loading = false,
  onSort,
  onFilter,
  onPageChange,
  onPageSizeChange,
  currentPage = 1,
  totalPages = 1,
  totalCount = 0,
  pageSize = 10,
  className = ''
}) {
  const safeLogs = Array.isArray(logs) ? logs : [];

  // Helper functions for formatting
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const getModuleBadge = (module) => {
    const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
    switch (module) {
      case 'user_management':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'role_management':
        return `${baseClasses} bg-purple-100 text-purple-800`;
      case 'job_management':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'content_management':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'invitation_management':
        return `${baseClasses} bg-pink-100 text-pink-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getStatusBadge = (success) => {
    const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
    return success
      ? `${baseClasses} bg-green-100 text-green-800`
      : `${baseClasses} bg-red-100 text-red-800`;
  };

  // Define table columns
  const columns = [
    {
      key: 'createdAt',
      label: 'Date & Time',
      accessor: 'createdAt',
      sortable: true,
      filterable: true,
      type: 'date',
      filterType: 'date',
      render: (value) => formatDate(value)
    },
    {
      key: 'user',
      label: 'User',
      accessor: 'userName',
      sortable: true,
      filterable: true,
      render: (_, log) => (
        <div>
          <div className="text-sm text-gray-900">{log.userName || '-'}</div>
          <div className="text-sm text-gray-500">{log.userEmail || '-'}</div>
        </div>
      )
    },
    {
      key: 'module',
      label: 'Module',
      accessor: 'module',
      sortable: true,
      filterable: true,
      render: (value) => (
        <span className={getModuleBadge(value)}>
          {value ? value.replace(/_/g, ' ') : '-'}
        </span>
      )
    },
    {
      key: 'success',
      label: 'Status',
      accessor: 'success',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'true', label: 'Success' },
        { value: 'false', label: 'Failed' }
      ],
      render: (value) => (
        <span className={getStatusBadge(value)}>
          {value ? 'Success' : 'Failed'}
        </span>
      )
    },
    {
      key: 'details',
      label: 'Details',
      sortable: false,
      filterable: false,
      render: (_, log) => (
        <div className="text-sm text-gray-500">
          {log.ipAddress && (
            <div className="text-xs">IP: {log.ipAddress}</div>
          )}
          {log.errorMessage && (
            <div className="text-xs text-red-600 max-w-xs truncate" title={log.errorMessage}>
              Error: {log.errorMessage}
            </div>
          )}
        </div>
      )
    }
  ];

  return (
    <DataTable
      data={safeLogs}
      columns={columns}
      loading={loading}
      onSort={onSort}
      onFilter={onFilter}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      currentPage={currentPage}
      totalPages={totalPages}
      totalCount={totalCount}
      pageSize={pageSize}
      searchable={false}
      filterable={false}
      sortable={true}
      selectable={false}
      className={className}
      emptyMessage="No audit logs found"
      showPagination={true}
      showSearch={false}
      showFilters={false}
      customActions={null}
      serverPagination={true}
      serverFiltering={true}
    />
  );
}
